import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Sun, Check, Sparkles } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { THEMES } from './themeStorage';
import { useLocale } from '../context/LocaleContext';

// Architectural split-circle icon for Hybrid mode
const HybridIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v18" />
    <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" opacity="0.45" />
  </svg>
);

const themeOptions = [
  { id: THEMES.DARK, label: 'Dark', labelUr: 'ڈارک', icon: Moon, desc: 'Deep luxury navy & champagne gold' },
  { id: THEMES.LIGHT, label: 'Light', labelUr: 'لائٹ', icon: Sun, desc: 'Warm architectural ivory & dark type' },
  { id: THEMES.HYBRID, label: 'Hybrid', labelUr: 'ہائبرڈ', icon: HybridIcon, desc: 'Curated alternating sections' },
];

export const ThemeSelector = ({ isMobile = false }) => {
  const { theme, setTheme } = useTheme();
  const { isRtl } = useLocale();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const currentOption = themeOptions.find((o) => o.id === theme) || themeOptions[2];
  const CurrentIcon = currentOption.icon;

  // Handle outside click & Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(e.target) && 
        document.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const selectTheme = (optId) => {
    setTheme(optId);
    setIsOpen(false);
  };

  // Mobile Segmented Row Variant (for mobile drawer)
  if (isMobile) {
    return (
      <div className="w-full flex flex-col gap-2 pt-2 border-t border-theme">
        <div className="flex items-center justify-between px-1">
          <span className="text-[10px] uppercase font-mono tracking-widest text-theme-gold font-bold">
            {isRtl ? 'ظاہری انداز (تھیم)' : 'APPEARANCE'}
          </span>
          <span className="text-[10px] font-mono text-theme-muted uppercase">
            {currentOption.label}
          </span>
        </div>
        <div 
          role="radiogroup" 
          aria-label="Website appearance"
          className="grid grid-cols-3 gap-2 bg-theme-surface border border-theme p-1 rounded-xl shadow-inner"
        >
          {themeOptions.map((opt) => {
            const Icon = opt.icon;
            const isSelected = theme === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => selectTheme(opt.id)}
                className={`min-h-[44px] flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-[#B99A5B] text-[#070C12] shadow-md font-extrabold scale-[1.02]'
                    : 'text-theme-muted hover:text-theme-primary hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="whitespace-nowrap">{isRtl ? opt.labelUr : opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Header Luxury Icon Button Variant (Desktop & Mobile Header)
  return (
    <div ref={containerRef} className="relative inline-block text-left shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Current appearance: ${currentOption.label}. Click to switch theme.`}
        title={`Appearance: ${currentOption.label}`}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`h-10 px-2.5 sm:px-3 rounded-xl border transition-all duration-200 flex items-center gap-2 text-xs font-mono font-bold tracking-wider uppercase cursor-pointer ${
          isOpen
            ? 'border-[#B99A5B] bg-theme-surface text-theme-gold shadow-[0_0_12px_rgba(185,154,91,0.35)]'
            : 'border-theme bg-theme-surface hover:border-[#B99A5B] text-theme-primary shadow-theme hover:bg-theme-page'
        }`}
      >
        <CurrentIcon className="w-4 h-4 text-theme-gold transition-transform duration-300" />
        <span className="hidden xl:inline text-[11px] font-mono text-theme-primary/90">{currentOption.label}</span>
      </button>

      {/* Popover Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.14, ease: 'easeOut' }}
            role="radiogroup"
            aria-label="Website appearance"
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute right-0 mt-2 w-56 rounded-2xl bg-theme-surface border border-[#B99A5B]/50 shadow-2xl p-2 z-[100] overflow-hidden font-sans backdrop-blur-xl"
          >
            <div className="px-3 py-1.5 border-b border-theme mb-1.5 flex items-center justify-between">
              <span className="text-[9px] font-mono uppercase tracking-widest text-theme-gold font-bold">
                {isRtl ? 'ظاہری شکل منتخب کریں' : 'APPEARANCE MODE'}
              </span>
              <Sparkles className="w-3 h-3 text-theme-gold/70" />
            </div>

            <div className="space-y-1">
              {themeOptions.map((opt) => {
                const Icon = opt.icon;
                const isSelected = theme === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      selectTheme(opt.id);
                    }}
                    className={`w-full min-h-[44px] flex items-center justify-between px-3 py-2 rounded-xl text-xs transition-all cursor-pointer text-left ${
                      isSelected
                        ? 'bg-[#B99A5B] text-[#070C12] shadow-md font-extrabold'
                        : 'text-theme-muted hover:bg-theme-page hover:text-theme-primary'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Icon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-[#070C12]' : 'text-theme-gold'}`} />
                      <div className="flex flex-col">
                        <span className="font-bold">{isRtl ? opt.labelUr : opt.label}</span>
                        <span className={`text-[9px] line-clamp-1 ${isSelected ? 'text-[#070C12]/80' : 'text-theme-muted'}`}>
                          {opt.desc}
                        </span>
                      </div>
                    </div>
                    {isSelected && <Check className="w-4 h-4 shrink-0 ml-2 stroke-[3]" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThemeSelector;
